"use client";

import {
  createStatsigEvent,
  STATSIG_EVENTS,
  useStatsigClient,
} from "@repo/statsig";
import LoadingSkeleton from "@repo/ui/LoadingSkeleton";
import Image from "next/image";
import { useRouter } from "next/navigation";
import owletHeaderImage from "public/owlet-header.png";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useFeltData } from "src/app/home/hooks/use-felt-data";
import { OwletEmptyState } from "src/components/owlet-empty-state";
import { SmallLabelText } from "src/components/owlet-typography";
import { ErrorCodes, Routes } from "src/lib/routes";
import {
  updateSessionAfterSignup,
  updateSessionForExistingAccount,
} from "src/lib/session-actions";
interface ValidationError {
  hasError: true;
  code: string;
  message: string;
}

// Constants
const ERROR_TITLES = {
  [ErrorCodes.NO_INITIAL_CONTEXT]: "Launch from Native App",
  [ErrorCodes.PROFILE_INCOMPLETE]: "Configuration Error",
} as const;

const createErrorParams = (code: string, message: string) => {
  const params = new URLSearchParams();
  params.set("code", code);
  params.set("message", message);
  return params.toString();
};

const UNKNOWN_ERROR_MESSAGE =
  "Something went wrong. Please try again, or use Debug for more information.";

const toValidationError = (error: unknown): ValidationError => {
  if (error instanceof Error && error.message.trim().length > 0) {
    return {
      hasError: true,
      code: ErrorCodes.PROFILE_INCOMPLETE,
      message: error.message,
    };
  }

  return {
    hasError: true,
    code: ErrorCodes.PROFILE_INCOMPLETE,
    message: UNKNOWN_ERROR_MESSAGE,
  };
};

// Components
const OwletHeader = ({ showBeta = false }: { showBeta?: boolean }) => (
  <div className="flex flex-col items-center py-6">
    <div className="relative">
      <Image
        src={owletHeaderImage}
        alt="Owlet Clinic"
        height={18}
        width={114}
        priority
      />
      {showBeta && (
        <div className="absolute -top-1 -right-8 bg-purple-500 text-white text-xs px-2 py-0.5 rounded-full">
          BETA
        </div>
      )}
    </div>
    <SmallLabelText className="text-purple-accent mt-2">
      Pediatricians 24/7 in under an hour
    </SmallLabelText>
  </div>
);

const ErrorBar = ({
  error,
  onMoreInfo,
}: {
  error: ValidationError;
  onMoreInfo: () => void;
}) => (
  <div className="bg-red-600 text-white p-4 flex items-center justify-between">
    <div className="flex items-center gap-3">
      <svg
        className="w-5 h-5 text-red-200"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      </svg>
      <div>
        <div className="font-semibold">
          {ERROR_TITLES[error.code as keyof typeof ERROR_TITLES] ||
            "Configuration Error"}
        </div>
        <div className="text-sm text-red-100">{error.message}</div>
      </div>
    </div>
    <div className="flex items-center gap-2">
      <a
        href={Routes.DEBUG}
        className="bg-red-700 hover:bg-red-800 px-3 py-1 rounded text-sm font-medium transition-colors"
      >
        Debug
      </a>
      <button
        onClick={onMoreInfo}
        className="bg-red-700 hover:bg-red-800 px-3 py-1 rounded text-sm font-medium transition-colors"
      >
        More Info
      </button>
    </div>
  </div>
);

const EmptyState = ({
  onGetCare,
  isProcessing,
}: {
  onGetCare: () => void;
  isProcessing: boolean;
}) => (
  <div className="min-h-screen bg-[#060727]">
    <OwletHeader showBeta={true} />
    <div className="p-4 pt-[20px] pb-0 flex flex-col gap-6">
      <OwletEmptyState onGetCare={onGetCare} isProcessing={isProcessing} />
    </div>
  </div>
);

export default function RootClient() {
  const router = useRouter();
  const { logEvent } = useStatsigClient();
  const [isProcessing, startTransition] = useTransition();
  const [pageError, setPageError] = useState<ValidationError | null>(null);
  const hasAttemptedSignup = useRef(false);

  // Get felt data which includes device context and provider context
  const {
    data: feltData,
    isLoading: isFeltDataLoading,
    registerAccount,
  } = useFeltData();

  // Extract device context (same as old initialContext)
  const initialContext = feltData?.deviceContext || null;

  // Validate context and email
  const validationError = useMemo((): ValidationError | null => {
    if (isFeltDataLoading) {
      return null;
    }

    if (!initialContext) {
      return {
        hasError: true,
        code: ErrorCodes.NO_INITIAL_CONTEXT,
        message: "Please launch this app from the Owlet native application.",
      };
    }
    if (!initialContext.account) {
      return {
        hasError: true,
        code: ErrorCodes.NO_INITIAL_CONTEXT,
        message:
          "Account information is missing. Please try again from the native app.",
      };
    }

    return null;
  }, [initialContext, isFeltDataLoading]);

  const visibleError = pageError ?? validationError;

  const handleSignupOrSessionResume = async () => {
    if (!initialContext?.account) {
      setPageError({
        hasError: true,
        code: ErrorCodes.NO_INITIAL_CONTEXT,
        message: "Cannot sign up without account context.",
      });
      return;
    }

    setPageError(null);

    startTransition(async () => {
      try {
        // Check if account already exists via server action
        const existingResult = await updateSessionForExistingAccount(
          initialContext.account.id
        );

        if (existingResult.success) {
          router.push(Routes.HOME);
          return;
        }

        const { accountId, primaryAccountHolderId } = await registerAccount();

        if (!accountId) throw new Error("Failed to create account");

        // Log account creation event
        logEvent(
          createStatsigEvent(STATSIG_EVENTS.ACCOUNT_CREATED, {
            accountId,
            email: initialContext?.account?.email,
          })
        );

        // Update session and navigate to home
        const result = await updateSessionAfterSignup(
          accountId,
          primaryAccountHolderId,
          initialContext.account.id
        );

        if (result.success) {
          router.push(Routes.HOME);
        } else {
          console.error("Failed to update session:", result.error);
          setPageError({
            hasError: true,
            code: ErrorCodes.PROFILE_INCOMPLETE,
            message: result.error || UNKNOWN_ERROR_MESSAGE,
          });
        }
      } catch (error) {
        // Handle case where account already exists
        if (
          error instanceof Error &&
          error.message === "Account already exists"
        ) {
          console.log(
            "Account already exists, updating session and redirecting to home"
          );
          logEvent(
            createStatsigEvent(STATSIG_EVENTS.ACCOUNT_EXISTING_RETURN, {
              accountId: initialContext.account.id,
              email: initialContext?.account?.email,
            })
          );

          // Update session with existing account info before navigating
          try {
            const result = await updateSessionForExistingAccount(
              initialContext.account.id
            );

            if (result.success) {
              router.push(Routes.HOME);
            } else {
              console.error(
                "Failed to update session for existing account:",
                result.error
              );
              setPageError({
                hasError: true,
                code: ErrorCodes.PROFILE_INCOMPLETE,
                message: result.error || UNKNOWN_ERROR_MESSAGE,
              });
            }
          } catch (sessionError) {
            console.error(
              "Error updating session for existing account:",
              sessionError
            );
            setPageError(toValidationError(sessionError));
          }
          return;
        }

        // Handle other errors
        console.error("Failed to complete signup:", error);
        setPageError(toValidationError(error));
      }
    });
  };

  // Check if user is already registered - if so, redirect to home
  useEffect(() => {
    if (isFeltDataLoading) return;
    if (validationError) return;
    if (hasAttemptedSignup.current) return;

    hasAttemptedSignup.current = true;
    void handleSignupOrSessionResume();
  }, [isFeltDataLoading, validationError]);

  const handleErrorMoreInfo = () => {
    if (!visibleError) return;
    router.push(
      `${Routes.ERROR}?${createErrorParams(visibleError.code, visibleError.message)}`
    );
  };

  if (visibleError) {
    return (
      <div className="min-h-screen bg-[#060727]">
        <ErrorBar error={visibleError} onMoreInfo={handleErrorMoreInfo} />
        <EmptyState
          onGetCare={() => {
            hasAttemptedSignup.current = false;
            void handleSignupOrSessionResume();
          }}
          isProcessing={isProcessing}
        />
      </div>
    );
  }

  return <LoadingSkeleton />;
}
